import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { getPaymentStatus } from "@/lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = sp.get("session_id");
  const orderId = sp.get("order_id");
  const [status, setStatus] = useState("polling"); // polling | paid | expired | error
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    let n = 0;
    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await getPaymentStatus(sessionId);
        if (r.payment_status === "paid") { setStatus("paid"); return; }
        if (r.payment_status === "expired" || r.status === "expired") { setStatus("expired"); return; }
      } catch {}
      n += 1;
      setAttempts(n);
      if (n > 20) { setStatus("expired"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-card border border-foreground brutal-shadow p-10 text-center">
        {status === "polling" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin mb-4" strokeWidth={1.5}/>
            <h1 className="text-2xl font-bold tracking-tight mb-2">Confirming payment…</h1>
            <p className="text-muted-foreground text-sm">Poll {attempts}/20 · Please wait, this usually takes 2-5 seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-primary mb-4" strokeWidth={2}/>
            <div className="overline text-primary mb-2">◆ ORDER CONFIRMED</div>
            <h1 className="text-3xl font-bold tracking-tight mb-3">Payment complete</h1>
            <p className="text-muted-foreground mb-6">Your seller has been notified. Track progress from your order page.</p>
            <button
              data-testid="payment-view-order"
              onClick={() => navigate(`/order/${orderId}`)}
              className="w-full bg-primary text-primary-foreground py-3 mono text-sm hover:bg-primary/90"
            >VIEW ORDER →</button>
          </>
        )}
        {status === "expired" && (
          <>
            <h1 className="text-2xl font-bold tracking-tight mb-3">Payment status uncertain</h1>
            <p className="text-muted-foreground text-sm mb-6">We couldn't confirm within the window. Check your dashboard or try again.</p>
            <Link to="/dashboard" className="inline-block border border-foreground px-6 py-2.5 mono text-xs">GO TO DASHBOARD</Link>
          </>
        )}
        {status === "error" && (
          <>
            <h1 className="text-2xl font-bold tracking-tight mb-3">Missing session</h1>
            <Link to="/dashboard" className="inline-block border border-foreground px-6 py-2.5 mono text-xs">GO TO DASHBOARD</Link>
          </>
        )}
      </div>
    </div>
  );
}
